import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  team: null,
  stores: [],
  loading: true,
  refresh: async () => {},
  refreshTeam: async () => {},
  switchStore: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null); // { store, role, permissions, is_guest }
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshTeam = useCallback(async () => {
    try {
      const t = await api.teamMe();
      setTeam(t);
      const s = await api.teamMyStores();
      setStores(s.stores || []);
    } catch (_e) {
      setTeam(null);
      setStores([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.authMe();
      setUser(me);
    } catch (_e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
    // Always load team info (covers guest too)
    await refreshTeam();
  }, [refreshTeam]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const switchStore = useCallback(async (storeId) => {
    await api.teamSwitch(storeId);
    await refreshTeam();
    // Force-refresh the page so all data is re-fetched with the new store cookie
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, [refreshTeam]);

  const logout = useCallback(async () => {
    try {
      await api.authLogout();
    } catch (_e) { /* noop */ }
    setUser(null);
    setTeam(null);
    setStores([]);
    try { localStorage.removeItem("mag_guest"); } catch (_e) { /* noop */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, team, stores, loading, refresh, refreshTeam, switchStore, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Convenience hook for permission checks
export function usePerm(name) {
  const { team } = useAuth();
  if (!team) return false;
  if (team.role === "owner") return true;
  return !!team.permissions?.[name];
}
